import sessionService from '@/app/features/ams/common/session-service';
import { useClientStore } from '@/app/features/ams/client/client-store';
import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
import { Roles } from '@/app/shared/enums/roles.enum';
import { authUrls, oktaAuth } from '@/app/shared/okta/auth';
import { ability } from '@/app/shared/services/ability-service';
import type {
  Actions,
  AppSubjects,
  AMSSubjects,
  AdminSubjects,
  ToolsSubject,
  ViewerSubjects,
} from '@/app/shared/services/ability-service';
import authApi, {
  formLogin,
  logout,
  refreshToken,
} from '@/app/shared/services/auth-service';
import { AuthenticationState } from '@/app/shared/types/stores/auth-state.interface';
import { AuthState } from '@okta/okta-auth-js';
import { defineStore } from 'pinia';
import AxiosProvider from '@/app/utils/api/axios-provider';
import { useFeatureStore } from './feature-store';

type Subjects =
  | AppSubjects
  | AMSSubjects
  | AdminSubjects
  | ToolsSubject
  | ViewerSubjects;

/* eslint-disable no-unused-vars */
export enum AuthStatus {
  DEFAULT = '',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}
/* eslint-enable no-unused-vars */

// This is required because of an auth.js default behaviour explained in https://github.com/okta/okta-auth-js#expireearlyseconds
const oktaJwtEarlyExpirationDefaultInSeconds = 30;
const defaultErrorDisplayTimeInMs = 45000;

export const useAuthStore = defineStore({
  id: 'authStoreId',
  state: () =>
    ({
      status: AuthStatus.DEFAULT,
      authMethod: '',
      roles: [],
      name: '',
      username: '',
      jwtExpirationTime: 0,
      atbAuthorizationError: false,
      preventExpiryWarning: false,
      exchangeServiceJwt: '',
      formAuthJwtAccessToken: '',
      isInitializing: false,
      sessionRestoreAttempted: false,
      userRolesData: [] as string[],
    } as AuthenticationState),

  getters: {
    isAuthenticated(): boolean {
      return this.status === AuthStatus.SUCCESS;
    },
    isAtbAuthorizationError(): boolean {
      return this.atbAuthorizationError;
    },
    errorDisplayTimeinMs(): number {
      return defaultErrorDisplayTimeInMs;
    },
    exchangeServiceToken(): string {
      return this.exchangeServiceJwt;
    },
    shouldUseFormLogin(): boolean {
      return this.authMethod === 'form';
    },
    shouldUseOktaLogin(): boolean {
      return this.authMethod === 'sso';
    },
  },

  actions: {
    async getLoginProfile() {
      try {
        const response = await authApi.getLoginProfile();
        const authMethod = response.data.data;
        return authMethod;
      } catch (error) {
        console.error('Failed to get login profile:', error);
        return 'form';
      }
    },
    useApigeeFlow() {
      const exchangeServiceUrl = config.VUE_APP_TOKEN_EXCHANGE_SERVICE_URL;
      return exchangeServiceUrl !== undefined && exchangeServiceUrl != '';
    },

    async updateLoginInfo(authState: AuthState) {
      const nameClaim = 'name';
      const preferredUsernameClaim = 'preferred_username';
      const expClaim = 'exp';

      try {
        const authenticated = authState.isAuthenticated;
        this.exchangeServiceJwt = '';

        if (authenticated) {
          const idToken = authState.idToken;
          const accessToken = authState.accessToken;
          this.name = idToken?.claims[nameClaim] || '';
          this.username = idToken?.claims[preferredUsernameClaim] || '';
          this.jwtExpirationTime =
            (accessToken?.claims[expClaim] || 0) -
            oktaJwtEarlyExpirationDefaultInSeconds;
          await this.updateRoles();

          this.status = AuthStatus.SUCCESS;
        } else {
          this.status = AuthStatus.DEFAULT;
          this.username = '';
          this.name = '';
          ability.update([]);
        }
      } catch (error) {
        console.error('Error retrieving login information', error);
      }
    },

    async getExchangeServiceToken() {
      const exchangeServiceUrl = config.VUE_APP_TOKEN_EXCHANGE_SERVICE_URL;

      return new Promise((resolve, reject) => {
        AxiosProvider.exchangeTokenServiceClient
          .post(exchangeServiceUrl)
          .then((response) => {
            this.exchangeServiceJwt = response.data.access_token;
            resolve(true);
          })
          .catch((err: any) => {
            console.error('Error retrieving exchange service token: ', err);
            reject(err);
          });
      });
    },

    async authLogout(fromAuthorizationError: boolean = false) {
      const clientStore = useClientStore();
      const systemStore = useSystemsStore();

      if (fromAuthorizationError) {
        this.atbAuthorizationError = true;
        await new Promise((f) => setTimeout(f, defaultErrorDisplayTimeInMs));
      }

      if (
        clientStore.selectedClient !== undefined &&
        clientStore.selectedClient.id != -1
      ) {
        try {
          await sessionService.forceLogout(clientStore.selectedClient.id);
        } catch (error) {
          console.error(
            'error releasing editing locks for the client Id',
            clientStore.selectedClient.id,
            'error:',
            error
          );
        }
      }

      this.atbAuthorizationError = false;
      this.userRolesData = [];
      if (this.shouldUseFormLogin) {
        await logout();
        window.onbeforeunload = null;
        window.location.assign(authUrls.loginUrl);
      } else if (this.shouldUseOktaLogin) {
        oktaAuth.signOut();
        localStorage.removeItem('okta-cache-storage');
        sessionStorage.clear();
      }

      return new Promise((resolve) => {
        clientStore.$reset();
        systemStore.$reset();
        this.$reset();
        resolve(true);
      });
    },
    async updateRoles(
      clientId?: number,
      appFilter?: 'ADMIN' | 'AMS',
      forceRefetch: boolean = false
    ) {
      if (this.userRolesData.length === 0 || forceRefetch) {
        const {
          data: { data },
        } = await authApi.getUserRoles();
        this.userRolesData = data;
      }

      const data = this.userRolesData;

      if (data.includes(Roles.SUPERUSER)) {
        return ability.update([{ action: 'WRITE', subject: 'ALL' }]);
      }

      ability.update([]);

      const authorities: Array<{ action: Actions; subject: Subjects }> = [];

      data.forEach((item: string) => {
        if (item.startsWith('ROLE_')) {
          return;
        }

        const parts = item.split('_');
        if (parts.length >= 4) {
          const itemClientId = parts[0];
          const app = parts[1];
          const right = parts.slice(2, -1).join('_');
          const access = parts[parts.length - 1];

          if (clientId !== undefined && itemClientId !== clientId.toString()) {
            return;
          }

          if (appFilter && app !== appFilter) {
            return;
          }

          authorities.push({
            action: access as Actions,
            subject: right as Subjects,
          });
        }
      });

      if (authorities.length > 0) {
        ability.update(authorities);
      }
    },
    async loginWithForm(username: string, password: string) {
      try {
        this.status = AuthStatus.LOADING;
        const response = await formLogin({ username, password });
        const token = response.data.data;
        this.formAuthJwtAccessToken = token;

        const parsedToken = this.parseJwtToken(token);
        this.name = parsedToken.sub || '';
        this.username = parsedToken.sub || '';
        this.jwtExpirationTime = parsedToken.exp || 0;
        await this.updateRoles();
        const featureStore = useFeatureStore();
        await featureStore.initializeFeatureStore();
        this.status = AuthStatus.SUCCESS;
        return true;
      } catch (error) {
        console.error(error);
        this.status = AuthStatus.ERROR;
        throw error;
      }
    },
    parseJwtToken(token: string): Record<string, any> {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) {
          throw new Error('Invalid token format');
        }

        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async validateStoredToken(token: string) {
      try {
        const parsedToken = this.parseJwtToken(token);
        const now = Math.floor(Date.now() / 1000);
        if (parsedToken.exp && parsedToken.exp > now) {
          this.formAuthJwtAccessToken = token;
          this.name = parsedToken.sub || '';
          this.username = parsedToken.sub || '';
          this.jwtExpirationTime = parsedToken.exp || 0;
          await this.updateRoles();
          this.status = AuthStatus.SUCCESS;
        } else {
          this.status = AuthStatus.ERROR;
        }
      } catch (error) {
        console.error(error);
        this.status = AuthStatus.ERROR;
      }
    },

    async refreshFormAccessToken() {
      try {
        const response = await refreshToken();
        const token = response.data.data;
        this.formAuthJwtAccessToken = token;

        const parsedToken = this.parseJwtToken(token);
        this.name = parsedToken.sub || '';
        this.username = parsedToken.sub || '';
        this.jwtExpirationTime = parsedToken.exp || 0;
        return true;
      } catch (error) {
        console.error('Form token refresh failed:', error);
        this.status = AuthStatus.ERROR;
        this.formAuthJwtAccessToken = '';
        return false;
      }
    },

    async refreshOktaAccessToken() {
      try {
        await oktaAuth.tokenManager.renew('accessToken');
        return true;
      } catch (error) {
        console.error('Okta token renewal failed:', error);
        return false;
      }
    },

    async restoreSession() {
      if (this.isInitializing) return false;
      this.isInitializing = true;
      try {
        const success = await this.refreshFormAccessToken();
        if (success) {
          await this.updateRoles();
          const featureStore = useFeatureStore();
          await featureStore.initializeFeatureStore();
          this.status = AuthStatus.SUCCESS;
        } else {
          this.status = AuthStatus.DEFAULT;
        }
        return success;
      } finally {
        this.isInitializing = false;
        this.sessionRestoreAttempted = true;
      }
    },

    async deleteAuthToken() {
      this.formAuthJwtAccessToken = '';
      await logout();
    },
  },
});
